import { Injectable, signal, inject, DestroyRef, isDevMode, WritableSignal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, timeout, catchError } from 'rxjs';
import {
  PricedItem,
  PricedClusterJewel,
  RawPricedItem,
  PriceHistoryEntry,
  Economy,
  EconomyState,
  EconomyStateFile,
  ManualPricedItem,
  PriceType,
  InflationData,
  Economies
} from '../models/economy-models';
import {
  URL_ECONOMY_DATA,
  FILE_NAME_CURRENCY,
  FILE_NAME_UNIQUES,
  FILE_NAME_CLUSTER_JEWELS,
  LOCAL_STORAGE_KEY_ECO_TESTING_MODE,
} from '../const';
import { IdbStorageService, IdbStorage } from './idb-storage.service';
import { FilterStoreService } from './filter-store.service';
import { ItemDataService } from './item-data.service';
import { game, poe1, poe2 } from '../../game';
import { ToastService } from './toast.service';
import {
  estimateDivCardRewardValue,
} from '../models/div-card-reward-value';

const FETCH_STATUS_TIMEOUT = 2000; // 2 seconds
const FETCH_DATA_TIMEOUT = 2000; // 2 seconds
const PERIODIC_CHECK_INTERVAL = 10 * 60 * 1000;
const TESTING_MODE_CHECK_INTERVAL = 1 * 60 * 1000;
const DIV_CARD_REWARD_CAP_MIN_DIVINE = 200; // skip cap for cards worth ~1div+ (gambling premium)

interface EconomyDataType {
  name: string;
  data: WritableSignal<Map<Economy, PricedItem[]>>;
  previousData: WritableSignal<Map<Economy, PricedItem[]>>;
  pendingData: WritableSignal<Map<Economy, PricedItem[]>>;
  pendingTs: string | null;
  latestTs: string | null;
  previousTs: string | null;
  fixedValues: string[];
  idbPrefix: string;
  applyManualAndBackup: (data: PricedItem[], applyFixedValues?: boolean, applyLeagueValues?: boolean, applyDivCardFloors?: boolean) => void;
}

type StateFileKey = 'currency' | 'uniques' | 'clusterJewels';

interface EconomyStateSpec {
  filename: string;
  key: StateFileKey;
  def: EconomyDataType;
}

const SHARDS = poe2 ? [
  { name: "Regal Shard", basedOn: 'Regal Orb', fraction: 0.1 },
  { name: "Chance Shard", basedOn: "Orb of Chance", fraction: 0.1 },
 ] : [
  { name: "Exalted Shard", basedOn: 'Exalted Orb', fraction: 0.05 },
]

/** Old shared economy DB name; deleted on PoE1 init so `poe1-economy` is used instead. */
const LEGACY_SHARED_ECONOMY_DB = 'poe-economy';

@Injectable({
  providedIn: 'root'
})
export class EconomyService {
  private http = inject(HttpClient);
  private destroyRef = inject(DestroyRef);
  private periodicCheckTimer?: number;

  // In-memory signals for current active data
  currencyData = signal<Map<Economy, PricedItem[]>>(new Map());
  uniquesData = signal<Map<Economy, PricedItem[]>>(new Map());
  clusterJewelsData = signal<Map<Economy, PricedItem[]>>(new Map());

  // In-memory signals for previous data (for comparisons)
  previousCurrencyData = signal<Map<Economy, PricedItem[]>>(new Map());
  previousUniquesData = signal<Map<Economy, PricedItem[]>>(new Map());
  previousClusterJewelsData = signal<Map<Economy, PricedItem[]>>(new Map());

  // In-memory signals for pending updates (downloaded but not applied)
  pendingCurrencyData = signal<Map<Economy, PricedItem[]>>(new Map());
  pendingUniquesData = signal<Map<Economy, PricedItem[]>>(new Map());
  pendingClusterJewelsData = signal<Map<Economy, PricedItem[]>>(new Map());

  // UI notification signals
  hasPendingUpdates = signal<boolean>(false);
  updatedOnLaunch = signal<boolean>(false);
  isInitialized = signal<boolean>(false);

  // Inflation data signals
  inflation = signal<number | undefined>(undefined);
  inflationData = signal<InflationData | undefined>(undefined);

  private currencyEconomyData!: EconomyDataType;
  private uniquesEconomyData!: EconomyDataType;
  private clusterJewelsEconomyData!: EconomyDataType;

  currencyValuesFixed: string[] = [];
  uniquesValuesFixed: string[] = [];
  manualCurrencyValues: ManualPricedItem[] = [];
  manualUniquesValues: ManualPricedItem[] = [];
  veryLowValueDivCards: string[] = [];
  lowValueDivCards: string[] = [];

  lastUpdateType?: 'launch' | 'update';
  lastUpdateSyncing?: boolean;
  lastUpdateWasSynced?: boolean;
  lastUpdateHadSyncError?: boolean;

  private economyDb: IdbStorage;

  constructor(
    private filterStoreService: FilterStoreService,
    private itemDataService: ItemDataService,
    private toastService: ToastService,
    idbStorageService: IdbStorageService,
  ) {
    this.economyDb = idbStorageService.createStorage(`${game}-economy`);
    this.currencyValuesFixed = itemDataService.getCurrencyValuesFixed();
    this.manualCurrencyValues = itemDataService.getManualCurrencyValues();
    this.uniquesValuesFixed = itemDataService.getUniquesValuesFixed();
    this.manualUniquesValues = itemDataService.getManualUniquesValues();
    this.veryLowValueDivCards = itemDataService.getVeryLowValueDivCards();
    this.lowValueDivCards = itemDataService.getLowValueDivCards();

    this.currencyEconomyData = {
      name: 'currency',
      data: this.currencyData,
      previousData: this.previousCurrencyData,
      pendingData: this.pendingCurrencyData,
      pendingTs: null,
      latestTs: null,
      previousTs: null,
      fixedValues: this.currencyValuesFixed,
      idbPrefix: 'currency',
      applyManualAndBackup: (data, applyFixedValues?, applyLeagueValues?, applyDivCardFloors?) =>
        this.applyManualAndBackupCurrencyValues(data, applyFixedValues, applyLeagueValues, applyDivCardFloors),
    };

    this.uniquesEconomyData = {
      name: 'uniques',
      data: this.uniquesData,
      previousData: this.previousUniquesData,
      pendingData: this.pendingUniquesData,
      pendingTs: null,
      latestTs: null,
      previousTs: null,
      fixedValues: this.uniquesValuesFixed,
      idbPrefix: 'uniques',
      applyManualAndBackup: (data, applyFixedValues?, applyLeagueValues?) =>
        this.applyManualAndBackupUniquesValues(data, applyFixedValues, applyLeagueValues),
    };

    this.clusterJewelsEconomyData = {
      name: 'clusterJewels',
      data: this.clusterJewelsData,
      previousData: this.previousClusterJewelsData,
      pendingData: this.pendingClusterJewelsData,
      pendingTs: null,
      latestTs: null,
      previousTs: null,
      fixedValues: [],
      idbPrefix: 'clusterJewels',
      applyManualAndBackup: () => {},
    };
  }

  /**
   * Initialize the service - called by APP_INITIALIZER
   */
  async initialize(): Promise<void> {
    await this.deleteLegacySharedEconomyDbIfPoe1();
    await this.loadDataFromStorage();
    this.removeOldLocalStorageEntries();

    try {
      await this.checkAndUpdateOnStartup();
    } catch (error) {
      console.error('EconomyManagerService initialization error:', error);
    }

    this.setFixedValues();
    this.applyDivCardRewardCapsToAllMarketEconomies();
    this.isInitialized.set(true);
    this.startPeriodicChecks();

    this.calculateInflation();
  }

  /** Drop legacy `poe-economy` on PoE1 so only `poe1-economy` remains (no migration). */
  private async deleteLegacySharedEconomyDbIfPoe1(): Promise<void> {
    if (!poe1) return;
    try {
      await new Promise<void>((resolve, reject) => {
        const req = indexedDB.deleteDatabase(LEGACY_SHARED_ECONOMY_DB);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
        req.onblocked = () => resolve();
      });
    } catch (error) {
      console.error('Failed to delete legacy shared economy IndexedDB:', error);
    }
  }

  private removeOldLocalStorageEntries(): void {
    const prefix = poe1 ? 'poe1' : 'poe';
    if (!localStorage.getItem(`${prefix}-currency-latest-ts`)) return;

    const categories = ['currency', 'uniques', 'cluster-jewels'];
    const suffixes = [
      'values-latest-league', 'values-latest-league-hc', 'values-latest-standard', 'values-latest-standard-hc', 'latest-ts',
      'values-previous-league', 'values-previous-league-hc', 'values-previous-standard', 'values-previous-standard-hc', 'previous-ts',
    ];
    for (const cat of categories) {
      for (const suf of suffixes) {
        localStorage.removeItem(`${prefix}-${cat}-${suf}`);
      }
    }
    // Legacy keys
    for (const type of ['currency', 'unique']) {
      localStorage.removeItem(`${prefix}-latest-${type}-values`);
      localStorage.removeItem(`${prefix}-latest-${type}-values-date`);
      localStorage.removeItem(`${prefix}-previous-${type}-values`);
      localStorage.removeItem(`${prefix}-previous-${type}-values-date`);
    }
  }

  private setFixedValues(): void {
    [this.currencyEconomyData, this.uniquesEconomyData].forEach(def => this.setFixedValuesForCategory(def));
  }

  private setFixedValuesForCategory(def: EconomyDataType): void {
    const items = def.fixedValues.map(value => {
      const [name, price] = value.split('|');
      return { name, price: parseFloat(price), ts: new Date().toISOString(), type: PriceType.HighConfidence } as PricedItem;
    });
    // Fixed already has curated values; skip market very-low/low div-card floors.
    def.applyManualAndBackup(items, false, true, false);
    def.data.update(data => {
      data.set(Economy.Fixed, items);
      return data;
    });
  }

  /**
   * Check state files and update on startup (automatically applies if newer)
   */
  private async checkAndUpdateOnStartup(): Promise<void> {
    const specStates = await Promise.all(this.getStateSpecs().map(spec => this.fetchStateForSpec(spec)));
    const updates = specStates.filter(({ spec, state }) => !spec.def.latestTs || state.ts > spec.def.latestTs);
    if (updates.length === 0) return;

    const updatePromises = updates.map(({ spec, state }) => this.fetchAndApplyData(state, spec.def));
    await Promise.all(updatePromises);

    this.setLastUpdateType('launch', false, false);
    this.updatedOnLaunch.set(true);
  }

  private getStateSpecs(): EconomyStateSpec[] {
    const specs: EconomyStateSpec[] = [
      { filename: FILE_NAME_CURRENCY, key: 'currency', def: this.currencyEconomyData },
      { filename: FILE_NAME_UNIQUES, key: 'uniques', def: this.uniquesEconomyData },
    ];
    if (poe1) {
      specs.push({ filename: FILE_NAME_CLUSTER_JEWELS, key: 'clusterJewels', def: this.clusterJewelsEconomyData });
    }
    return specs;
  }

  private async fetchStateForSpec(spec: EconomyStateSpec): Promise<{ spec: EconomyStateSpec; state: EconomyState }> {
    const stateFile = await this.fetchStateFile<EconomyStateFile>(spec.filename);
    const stateLinks = stateFile[spec.key];
    if (!stateLinks) {
      throw new Error(`Missing "${spec.key}" in economy state file: ${spec.filename}`);
    }

    return {
      spec,
      state: {
        ts: stateFile.ts,
        ...stateLinks,
      },
    };
  }

  setLastUpdateType(type: 'launch' | 'update', synced: boolean, hadSyncError: boolean = false): void {
    this.lastUpdateType = type;
    this.lastUpdateWasSynced = synced;
    this.lastUpdateHadSyncError = hadSyncError;
  }

  /**
   * Check for updates periodically (downloads but doesn't apply)
   */
  async checkForUpdates(): Promise<void> {
    try {
      const specStates = await Promise.all(this.getStateSpecs().map(spec => this.fetchStateForSpec(spec)));
      const updates = specStates.filter(({ spec, state }) => !spec.def.latestTs || state.ts > spec.def.latestTs);
      if (updates.length === 0) return;

      const fetches = updates.map(async ({ spec, state }) => {
        await this.fetchPendingData(state, spec.def);
        spec.def.pendingTs = state.ts;
      });
      await Promise.all(fetches);
      this.hasPendingUpdates.set(true);
    } catch (error) {
      console.error('Error checking for updates:', error);
    }
  }

  /**
   * Apply pending updates (user-triggered)
   */
  applyPendingUpdates(): void {
    this.applyPendingForCategory(this.currencyEconomyData);
    this.applyPendingForCategory(this.uniquesEconomyData);
    if (poe1) this.applyPendingForCategory(this.clusterJewelsEconomyData);
    // Signals are already updated by rotateAndStoreData, just recalculate inflation
    this.hasPendingUpdates.set(false);
    this.applyDivCardRewardCapsToAllMarketEconomies();
    this.calculateInflation();
  }

  private applyPendingForCategory(def: EconomyDataType): void {
    const pendingData = def.pendingData();
    if (pendingData.size > 0) {
      this.rotateAndStoreData(pendingData, def.pendingTs!, def);
      def.pendingData.set(new Map());
      def.pendingTs = null;
    }
  }

  private async fetchData(state: Economies, def: EconomyDataType): Promise<Map<Economy, PricedItem[]>> {
    const dataMap = new Map<Economy, PricedItem[]>();
    const entries: [string, Economy][] = [
      [state.league ?? '', Economy.League],
      [state.leagueHardcore ?? '', Economy.LeagueHardcore],
      [state.standard ?? '', Economy.Standard],
      [state.standardHardcore ?? '', Economy.StandardHardcore],
    ];
    for (const [filename, economy] of entries) {
      if (filename) {
        const data = this.deserializeItems(await this.fetchDataFile<RawPricedItem[]>(filename));
        def.applyManualAndBackup(data);
        dataMap.set(economy, data);
      }
    }
    return dataMap;
  }

  private async fetchAndApplyData(state: EconomyState, def: EconomyDataType): Promise<void> {
    const dataMap = await this.fetchData(state, def);
    this.rotateAndStoreData(dataMap, state.ts, def);
  }

  private async fetchPendingData(state: EconomyState, def: EconomyDataType): Promise<void> {
    const dataMap = await this.fetchData(state, def);
    def.pendingData.set(dataMap);
  }

  private applyManualAndBackupCurrencyValues(
    data: PricedItem[],
    applyFixedValues = true,
    applyLeagueValues = false,
    applyDivCardFloors = true,
  ): void {
    SHARDS.forEach(shard => {
      const existingShard = data.find(item => item.name === shard.name);
      const basedOn = data.find(item => item.name === shard.basedOn);
      if (basedOn) {
        const price = Math.round(basedOn.price * shard.fraction * 100) / 100;
        if (!existingShard) {
          data.push({ name: shard.name, price, ts: new Date().toISOString(), type: basedOn.type } as PricedItem);
        } else {
          existingShard.price = price;
          existingShard.type = basedOn.type;
        }
      }
    });

    this.manualCurrencyValues.forEach(manualItem => {
      const existingItem = data.find(item => item.name === manualItem.name);
      if (!existingItem) {
        data.push({ name: manualItem.name, price: manualItem.value, ts: new Date().toISOString(), type: PriceType.Manual } as PricedItem);
      } else {
        existingItem.price = manualItem.value;
        existingItem.type = PriceType.Manual;
      }
    });

    if (applyDivCardFloors) {
      this.veryLowValueDivCards.forEach(card => {
        const existingCard = data.find(v => v.name === card);
        if (!existingCard) {
          data.push({ name: card, price: 0.05, ts: new Date().toISOString(), type: PriceType.Capped } as PricedItem);
        } else {
          existingCard.price = 0.05;
          existingCard.type = PriceType.Capped;
        }
      });

      this.lowValueDivCards.forEach(card => {
        const existingCard = data.find(v => v.name === card);
        if (!existingCard) {
          data.push({ name: card, price: 0.25, ts: new Date().toISOString(), type: PriceType.Capped } as PricedItem);
        } else {
          existingCard.price = 0.25;
          existingCard.type = PriceType.Capped;
        }
      });
    }

    if (applyFixedValues) {
      this.currencyValuesFixed.forEach(value => {
        const [name, price] = value.split('|');
        const existingItem = data.find(item => item.name === name);
        if (!existingItem) {
          data.push({ name, price: parseFloat(price), ts: new Date().toISOString(), type: PriceType.FixedBecauseMissing } as PricedItem);
        }
      });
    }

    if (applyLeagueValues) {
      this.currencyData().get(Economy.League)?.forEach(value => {
        const existingItem = data.find(item => item.name === value.name);
        if (!existingItem) {
          data.push({ name: value.name, price: value.price, ts: value.ts, type: PriceType.LeagueBecauseMissing } as PricedItem);
        }
      });
    }
  }

  // Cap market div-card prices that exceed reward EV using that economy's live prices.
  private applyDivCardRewardCapsToAllMarketEconomies(): void {
    const currencyMap = this.currencyData();
    let changed = false;
    for (const [economy, data] of currencyMap) {
      if (economy === Economy.Fixed) continue;
      if (this.capDivCardsToRewardExpectedValue(data, economy)) changed = true;
    }
    if (changed) {
      this.currencyData.set(new Map(currencyMap));
    }
  }

  // Cap market div-card prices that exceed reward EV (skip Manual / Capped / FixedBecauseMissing / ≥1div).
  private capDivCardsToRewardExpectedValue(data: PricedItem[], economy: Economy): boolean {
    const uniqueData = this.uniquesData().get(economy) ?? [];
    const maps = {
      currency: new Map(data.map(item => [item.name, item.price])),
      unique: new Map(uniqueData.map(item => [item.name, item.price])),
    };

    let changed = false;
    for (const [cardName, reward] of this.itemDataService.getDivCardRewardsByCardName()) {
      const existing = data.find(item => item.name === cardName);
      if (!existing) continue;
      if (existing.type === PriceType.Manual || existing.type === PriceType.Capped || existing.type === PriceType.FixedBecauseMissing) continue;
      if (existing.price >= DIV_CARD_REWARD_CAP_MIN_DIVINE) continue;

      const expected = estimateDivCardRewardValue(reward, maps);
      if (expected == null || !Number.isFinite(expected)) continue;
      if (existing.price <= expected) continue;

      existing.price = Math.round(expected * 100) / 100;
      existing.type = PriceType.BasedOnReward;
      changed = true;
    }
    return changed;
  }

  private applyManualAndBackupUniquesValues(data: PricedItem[], applyFixedValues = true, applyLeagueValues = false): void {
    this.manualUniquesValues.forEach(manualItem => {
      const existingItem = data.find(item => item.name === manualItem.name);
      if (!existingItem) {
        data.push({ name: manualItem.name, price: manualItem.value, ts: new Date().toISOString(), type: PriceType.Manual } as PricedItem);
      }
    });

    if (applyFixedValues) {
      this.uniquesValuesFixed.forEach(value => {
        const [name, price] = value.split('|');
        const existingItem = data.find(item => item.name === name);
        if (!existingItem) {
          data.push({ name, price: parseFloat(price), ts: new Date().toISOString(), type: PriceType.FixedBecauseMissing } as PricedItem);
        }
      });
    }

    if (applyLeagueValues) {
      this.uniquesData().get(Economy.League)?.forEach(value => {
        const existingItem = data.find(item => item.name === value.name);
        if (!existingItem) {
          data.push({ name: value.name, price: value.price, ts: value.ts, type: PriceType.LeagueBecauseMissing } as PricedItem);
        }
      });
    }
  }

  private deserializeItems(raw: RawPricedItem[]): PricedItem[] {
    return raw.map(({ history, ...rest }) => ({
      ...rest,
      ...(history && {
        history: history.map(entry => {
          if (typeof entry !== 'string') return entry as unknown as PriceHistoryEntry;
          const [priceStr, ts, typeStr] = entry.split('|');
          return {
            price: parseFloat(priceStr),
            ts,
            ...(typeStr !== undefined && { type: parseInt(typeStr) as PriceType }),
          };
        }),
      }),
    }));
  }

  private serializeItems(items: PricedItem[]): RawPricedItem[] {
    return items.map(({ history, ...rest }) => ({
      ...rest,
      ...(history && {
        history: history.map(e =>
          e.type !== undefined ? `${e.price}|${e.ts}|${e.type}` : `${e.price}|${e.ts}`
        ),
      }),
    }));
  }

  private stripHistory(data: Map<Economy, PricedItem[]>): Map<Economy, PricedItem[]> {
    const stripped = new Map<Economy, PricedItem[]>();
    data.forEach((items, economy) => {
      stripped.set(economy, items.map(({ history: _, ...rest }) => rest));
    });
    return stripped;
  }

  /**
   * Rotate data: latest -> previous, new -> latest (in memory + persisted to IDB)
   */
  private rotateAndStoreData(newData: Map<Economy, PricedItem[]>, ts: string, def: EconomyDataType): void {
    const currentData = def.data();

    // Move current latest to previous in memory (without history entries)
    const strippedPrevious = this.stripHistory(currentData);
    def.previousData.set(strippedPrevious);
    def.previousTs = def.latestTs;

    // Set new data as latest in memory, preserving Fixed data
    const newMap = new Map(newData);
    const fixedData = currentData.get(Economy.Fixed);
    if (fixedData) newMap.set(Economy.Fixed, fixedData);
    def.data.set(newMap);
    def.latestTs = ts;

    // Persist to IDB (fire-and-forget)
    this.persistCategoryToIdb(def, newData, strippedPrevious);
  }

  private async persistCategoryToIdb(
    def: EconomyDataType,
    latestData: Map<Economy, PricedItem[]>,
    previousData: Map<Economy, PricedItem[]>,
  ): Promise<void> {
    const prefix = def.idbPrefix;
    try {
      const latestRecord: Record<string, RawPricedItem[]> = {};
      latestData.forEach((items, economy) => {
        latestRecord[economy] = this.serializeItems(items);
      });

      const previousRecord: Record<string, PricedItem[]> = {};
      previousData.forEach((items, economy) => {
        previousRecord[economy] = items;
      });

      await Promise.all([
        this.economyDb.idbSet(`${prefix}-latest`, latestRecord),
        this.economyDb.idbSet(`${prefix}-previous`, previousRecord),
        ...(def.latestTs ? [this.economyDb.idbSet(`${prefix}-latest-ts`, def.latestTs)] : []),
        ...(def.previousTs ? [this.economyDb.idbSet(`${prefix}-previous-ts`, def.previousTs)] : []),
      ]);
    } catch (error) {
      console.error(`Failed to persist ${def.name} data to IndexedDB:`, error);
    }
  }

  private async loadDataFromStorage(): Promise<void> {
    await Promise.all([
      this.loadCategoryFromStorage(this.currencyEconomyData),
      this.loadCategoryFromStorage(this.uniquesEconomyData),
      ...(poe1 ? [this.loadCategoryFromStorage(this.clusterJewelsEconomyData)] : []),
    ]);
  }

  private async loadCategoryFromStorage(def: EconomyDataType): Promise<void> {
    const prefix = def.idbPrefix;
    const latestMap = new Map<Economy, PricedItem[]>();
    const previousMap = new Map<Economy, PricedItem[]>();

    const [latestData, previousData, latestTs, previousTs] = await Promise.all([
      this.economyDb.idbGet<Record<string, RawPricedItem[]>>(`${prefix}-latest`),
      this.economyDb.idbGet<Record<string, PricedItem[]>>(`${prefix}-previous`),
      this.economyDb.idbGet<string>(`${prefix}-latest-ts`),
      this.economyDb.idbGet<string>(`${prefix}-previous-ts`),
    ]);

    if (latestData) {
      for (const [economy, items] of Object.entries(latestData)) {
        const deserialized = this.deserializeItems(items);
        def.applyManualAndBackup(deserialized);
        latestMap.set(economy as Economy, deserialized);
      }
    }

    if (previousData) {
      for (const [economy, items] of Object.entries(previousData)) {
        previousMap.set(economy as Economy, items);
      }
    }

    def.latestTs = latestTs ?? null;
    def.previousTs = previousTs ?? null;
    def.data.set(latestMap);
    def.previousData.set(previousMap);
  }

  /**
   * Calculate inflation based on Divine Orb price changes
   */
  private calculateInflation(): void {
    // Use League economy as default for inflation calculation
    const currentEconomy = this.filterStoreService.activeFilter()?.economy ?? Economy.League;
    const currentCurrencyData = this.currencyData().get(currentEconomy) || [];
    const previousCurrencyData = this.previousCurrencyData().get(currentEconomy) || [];

    const previousDivinePrice = previousCurrencyData.find(v => v.name === 'Divine Orb')?.price;
    const currentDivinePrice = currentCurrencyData.find(v => v.name === 'Divine Orb')?.price;

    if (previousDivinePrice && currentDivinePrice) {
      const inflationValue = currentDivinePrice / previousDivinePrice;
      this.inflation.set(inflationValue);

      const previousTs = this.currencyEconomyData.previousTs;
      const latestTs = this.currencyEconomyData.latestTs;

      const previousUniquesTs = this.uniquesEconomyData.previousTs;
      const latestUniquesTs = this.uniquesEconomyData.latestTs;

      const latestDate = latestTs
        ? new Date(latestTs).toLocaleString('en-GB')
        : new Date().toLocaleString('en-GB');
      const previousDate = previousTs
        ? new Date(previousTs).toLocaleString('en-GB')
        : latestDate;

      const latestUniquesDate = latestUniquesTs
        ? new Date(latestUniquesTs).toLocaleString('en-GB')
        : new Date().toLocaleString('en-GB');
      const previousUniquesDate = previousUniquesTs
        ? new Date(previousUniquesTs).toLocaleString('en-GB')
        : latestUniquesDate;

      const inflationData: InflationData = {
        previousDate,
        latestDate,
        previousDivinePrice,
        latestDivinePrice: currentDivinePrice,
        formatted: previousDivinePrice > currentDivinePrice
          ? `-${((previousDivinePrice - currentDivinePrice) / previousDivinePrice * 100).toFixed(2)}%`
          : `+${((currentDivinePrice - previousDivinePrice) / previousDivinePrice * 100).toFixed(2)}%`,
        color: previousDivinePrice > currentDivinePrice ? '#b12020 !important' : '#397439 !important',
        previousUniquesDate,
        latestUniquesDate,
      };

      this.inflationData.set(inflationData);
    } else {
      this.inflation.set(undefined);
      this.inflationData.set(undefined);
    }
  }

  /**
   * Check if economy testing mode is enabled (only in dev mode)
   */
  private isEconomyTestingMode(): boolean {
    if (!isDevMode()) {
      return false;
    }
    return localStorage.getItem(LOCAL_STORAGE_KEY_ECO_TESTING_MODE) === 'true';
  }

  /**
   * Fetch state file with timeout and cache busting
   */
  private async fetchStateFile<T>(filename: string): Promise<T> {
    // In development mode, check if testing mode is enabled
    if (this.isEconomyTestingMode()) {
      const localPath = `/tst/data/economy/${filename}`;
      try {
        const response = await fetch(localPath);
        if (!response.ok) {
          throw new Error(`Failed to fetch local file: ${localPath}`);
        }
        return await response.json();
      } catch (error) {
        console.error(`Error fetching local state file ${filename}:`, error);
        throw error;
      }
    }

    const url = `${URL_ECONOMY_DATA}${filename}?r=${Math.random()}`;
    return firstValueFrom(
      this.http.get<T>(url).pipe(
        timeout(FETCH_STATUS_TIMEOUT),
        catchError(error => {
          console.error(`Error fetching state file ${filename}:`, error);
          throw error;
        })
      )
    );
  }

  /**
   * Fetch data file with timeout
   */
  private async fetchDataFile<T>(filename: string): Promise<T> {
    // In development mode, check if testing mode is enabled
    if (this.isEconomyTestingMode()) {
      const localPath = `/tst/data/economy/${filename}`;
      try {
        const response = await fetch(localPath);
        if (!response.ok) {
          throw new Error(`Failed to fetch local file: ${localPath}`);
        }
        return await response.json();
      } catch (error) {
        console.error(`Error fetching local data file ${filename}:`, error);
        throw error;
      }
    }

    const url = `${URL_ECONOMY_DATA}${filename}`;
    return firstValueFrom(
      this.http.get<T>(url).pipe(
        timeout(FETCH_DATA_TIMEOUT),
        catchError(error => {
          console.error(`Error fetching data file ${filename}:`, error);
          throw error;
        })
      )
    );
  }

  /**
   * Get the current check interval based on testing mode
   */
  private getCheckInterval(): number {
    return this.isEconomyTestingMode() ? TESTING_MODE_CHECK_INTERVAL : PERIODIC_CHECK_INTERVAL;
  }

  /**
   * Start periodic checks
   */
  private startPeriodicChecks(): void {
    const scheduleCheck = () => {
      this.checkForUpdates();
      // Reschedule with current interval (allows dynamic switching)
      this.periodicCheckTimer = window.setTimeout(scheduleCheck, this.getCheckInterval());
    };

    // Start the first check
    this.periodicCheckTimer = window.setTimeout(scheduleCheck, this.getCheckInterval());

    // Cleanup on destroy
    this.destroyRef.onDestroy(() => {
      if (this.periodicCheckTimer) {
        clearTimeout(this.periodicCheckTimer);
      }
    });
  }

  // Public API methods

  getCurrencyData(): PricedItem[] {
    return this.getCategoryData(this.currencyEconomyData);
  }

  getUniquesData(): PricedItem[] {
    return this.getCategoryData(this.uniquesEconomyData);
  }

  getPreviousCurrencyData(): PricedItem[] {
    return this.getPreviousCategoryData(this.currencyEconomyData);
  }

  getPreviousUniquesData(): PricedItem[] {
    return this.getPreviousCategoryData(this.uniquesEconomyData);
  }

  getLatestTimestamp(type: 'currency' | 'uniques'): string | null {
    const def = type === 'currency' ? this.currencyEconomyData : this.uniquesEconomyData;
    return def.latestTs;
  }

  getPendingTimestamp(type: 'currency' | 'uniques'): string | null {
    const def = type === 'currency' ? this.currencyEconomyData : this.uniquesEconomyData;
    return def.pendingTs;
  }

  getCurrencyValue(name: string): PricedItem | undefined {
    return this.getCategoryValue(this.currencyEconomyData, name);
  }

  getPreviousCurrencyValue(name: string): PricedItem | undefined {
    return this.getPreviousCategoryValue(this.currencyEconomyData, name);
  }

  getUniqueValue(name: string): PricedItem | undefined {
    return this.getCategoryValue(this.uniquesEconomyData, name);
  }

  getPreviousUniqueValue(name: string): PricedItem | undefined {
    return this.getPreviousCategoryValue(this.uniquesEconomyData, name);
  }

  getClusterJewelsData(): PricedClusterJewel[] {
    return this.getCategoryData(this.clusterJewelsEconomyData) as PricedClusterJewel[];
  }

  getPreviousClusterJewelsData(): PricedClusterJewel[] {
    return this.getPreviousCategoryData(this.clusterJewelsEconomyData) as PricedClusterJewel[];
  }

  getClusterJewelValue(name: string): PricedClusterJewel | undefined {
    return this.getCategoryValue(this.clusterJewelsEconomyData, name) as PricedClusterJewel | undefined;
  }

  private getCategoryData(def: EconomyDataType): PricedItem[] {
    const activeEconomy = this.filterStoreService.activeFilter().economy;
    let data = def.data().get(activeEconomy);

    let usingFixedData = false;
    let usingLeagueData = false;
    if (!data && activeEconomy !== Economy.Fixed) {
      usingFixedData = true;
      data = def.data().get(Economy.Fixed);
    } else if (!data && activeEconomy === Economy.Fixed) {
      usingLeagueData = true;
      data = def.data().get(Economy.League);
    }

    if (!data) {
      const msg = `No ${def.name} data available for the active filter economy (${activeEconomy}), and no fixed or league data available.`;
      throw new Error(msg);
    } else if (def.name !== 'clusterJewels' && usingFixedData) {
      this.toastService.raise('warning', `No ${def.name} data available for the active filter economy (${activeEconomy}), using fixed data instead.`);
    } else if (def.name !== 'clusterJewels' && usingLeagueData) {
      this.toastService.raise('warning', `No ${def.name} data available for fixed economy, using league data instead.`);
    }

    return data;
  }

  private getPreviousCategoryData(def: EconomyDataType): PricedItem[] {
    return def.previousData().get(this.filterStoreService.activeFilter().economy) ?? [];
  }

  private getCategoryValue(def: EconomyDataType, name: string): PricedItem | undefined {
    return def.data().get(this.filterStoreService.activeFilter().economy)?.find(item => item.name === name);
  }

  private getPreviousCategoryValue(def: EconomyDataType, name: string): PricedItem | undefined {
    return def.previousData().get(this.filterStoreService.activeFilter().economy)?.find(item => item.name === name);
  }

  getInflation(): number | undefined {
    return this.inflation();
  }

  getInflationData(): InflationData | undefined {
    return this.inflationData();
  }

  recalculateInflation(): void {
    this.calculateInflation();
  }
}
